import { Icon } from '@dynamic/ui';
import { FC, ReactElement } from 'react';

import { PickerItemBaseProps } from '#/components/Picker/PickerItem';
import { horizontalDirection } from '#/service/constants';
import { tb } from '#/store/translation/translation.store';

import {
  isProductPickerDivider,
  ProductPickerItem,
} from '../PlanPurchaseFlow/PickProductStep/getPickerItems';
import { GroupDivider } from './GroupDivider';
import { ProductCard } from './ProductCard';
import { useProductSlider } from './ProductSlider.hook';
import {
  ArrowButton,
  CardWrapper,
  DividerWrapper,
  SliderContainer,
  SliderWrapper,
} from './ProductSlider.styled';

export interface ProductSliderProps {
  items: ProductPickerItem[];
  onContinue: (productId: string) => void;
  renderItem?: (
    item: PickerItemBaseProps,
    onContinue: (id: string) => void,
    autoFocus: boolean | undefined
  ) => ReactElement;
}

export const ProductSlider: FC<ProductSliderProps> = ({
  items,
  onContinue,
  renderItem,
}) => {
  const {
    sliderRef,
    canScrollLeft,
    canScrollRight,
    totalCards,
    showArrows,
    hasPromotionalLabel,
    scroll,
  } = useProductSlider({ items });

  const firstProductId = items.find(
    (pickerItem) => !isProductPickerDivider(pickerItem)
  )?.id;

  return (
    <SliderContainer>
      {showArrows && (
        <ArrowButton
          $direction={horizontalDirection.LEFT}
          $hasPromotionalLabel={hasPromotionalLabel}
          aria-label={tb('tts.arrow.LEFT')}
          data-testid="product-slider-arrow-left"
          disabled={!canScrollLeft}
          onClick={() => scroll(horizontalDirection.LEFT)}
        >
          <Icon name="chevron_left" />
        </ArrowButton>
      )}

      <SliderWrapper
        $hasPromotionalLabel={hasPromotionalLabel}
        $showArrows={showArrows}
        data-testid="product-slider-wrapper"
        ref={sliderRef}
      >
        {items.map((item, index) => {
          if (isProductPickerDivider(item)) {
            return (
              <DividerWrapper
                $totalCards={totalCards}
                key={item.id || `divider-${index}`}
              >
                <GroupDivider />
              </DividerWrapper>
            );
          }

          // Render regular items
          return (
            <CardWrapper
              $hasPromotionalLabel={Boolean(item.promotionalLabel)}
              key={item.id}
            >
              <ProductCard
                autoFocus={item.id === firstProductId}
                item={item}
                onContinue={onContinue}
                renderItem={renderItem}
              />
            </CardWrapper>
          );
        })}
      </SliderWrapper>

      {showArrows && (
        <ArrowButton
          $direction={horizontalDirection.RIGHT}
          $hasPromotionalLabel={hasPromotionalLabel}
          aria-label={tb('tts.arrow.RIGHT')}
          data-testid="product-slider-arrow-right"
          disabled={!canScrollRight}
          onClick={() => scroll(horizontalDirection.RIGHT)}
        >
          <Icon name="chevron_right" />
        </ArrowButton>
      )}
    </SliderContainer>
  );
};
